import React, { useState } from 'react';
import { 
  Box, TextField, Button, Typography, Container, Paper, Grid, CircularProgress, Link, InputAdornment 
} from '@mui/material';
import StorefrontIcon from '@mui/icons-material/Storefront';
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import MailOutlineIcon from '@mui/icons-material/MailOutline';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { registerUser } from '../Service/auth-service';
import { useNavigate } from 'react-router-dom';

function Register() {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    password_confirmation: ''
  });

  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    if (errorMsg) setErrorMsg('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (formData.password !== formData.password_confirmation) {
      setErrorMsg("Passwords do not match!");
      return;
    }

    setLoading(true);
    try {
      const data = await registerUser(formData);
      alert("Account created successfully!");
      navigate('/');
    } catch (error) {
      setErrorMsg(error.response?.data?.message || "Registration failed. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ 
      background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)', 
      minHeight: '100vh', 
      display: 'flex', 
      alignItems: 'center',
      py: 6 
    }}>
      <Container maxWidth="xs">
        <Paper elevation={0} sx={{ 
          p: 5, 
          borderRadius: 4, 
          boxShadow: '0 20px 40px rgba(0,0,0,0.08)',
          backgroundColor: '#fff'
        }}>
          {/* Header Section */}
          <Box sx={{ textAlign: 'center', mb: 4 }}>
            <Box sx={{ 
              display: 'inline-flex', 
              p: 2, 
              borderRadius: '50%', 
              bgcolor: '#e3f2fd', 
              color: '#2196f3',
              mb: 2 
            }}>
              <StorefrontIcon sx={{ fontSize: 40 }} />
            </Box>
            <Typography variant="h4" fontWeight="900" sx={{ color: '#2c3e50', letterSpacing: -0.5 }}>
              JOIN US
            </Typography>
            <Typography variant="body2" color="textSecondary" sx={{ mt: 1, fontWeight: 500 }}>
              Start managing your shops like a pro.
            </Typography>
          </Box>

          {errorMsg && (
            <Box sx={{ 
              bgcolor: '#ffebee', 
              color: '#c62828', 
              p: 1.5, 
              borderRadius: 2, 
              mb: 3, 
              textAlign: 'center',
              fontSize: '0.875rem',
              fontWeight: 600,
              border: '1px solid #ffcdd2'
            }}>
              {errorMsg}
            </Box>
          )}

          <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Full Name" name="name" variant="outlined"
                  value={formData.name} onChange={handleChange} required
                  InputProps={{ 
                    sx: { borderRadius: 2 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <PersonOutlineIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Email" name="email" type="email" variant="outlined"
                  value={formData.email} onChange={handleChange} required
                  InputProps={{ 
                    sx: { borderRadius: 2 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <MailOutlineIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Password" name="password" type="password" variant="outlined"
                  value={formData.password} onChange={handleChange} required
                  InputProps={{ 
                    sx: { borderRadius: 2 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth label="Confirm Password" name="password_confirmation" type="password" variant="outlined"
                  value={formData.password_confirmation} onChange={handleChange} required
                  error={formData.password_confirmation !== '' && formData.password !== formData.password_confirmation}
                  InputProps={{ 
                    sx: { borderRadius: 2 },
                    startAdornment: (
                      <InputAdornment position="start">
                        <LockOutlinedIcon fontSize="small" color="action" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <Button
                  fullWidth type="submit" variant="contained" size="large"
                  disabled={loading} disableElevation
                  sx={{ 
                    mt: 2, 
                    py: 1.8, 
                    borderRadius: 2, 
                    fontWeight: 800, 
                    bgcolor: '#2196f3',
                    fontSize: '1rem',
                    textTransform: 'none',
                    '&:hover': { bgcolor: '#1976d2' }
                  }}
                >
                  {loading ? <CircularProgress size={24} color="inherit" /> : 'Create Account'}
                </Button>
              </Grid>
            </Grid>
          </form>

          <Box sx={{ mt: 4, textAlign: 'center' }}>
            <Typography variant="body2" color="textSecondary" sx={{ fontWeight: 500 }}>
              Already have an account?{' '}
              <Link href="/" underline="none" sx={{ color: '#2196f3', fontWeight: 700 }}>
                Sign In
              </Link>
            </Typography>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
}

export default Register;